/**
 * CLC royalty CSV for Strawberry Patch retail orders.
 * Pure helpers: no Firestore. The upload header row must match Brand Manager 360 exactly.
 */

export const CLC_HEADERS = [
  'License_Code',
  'Institution_Short_Code',
  'Category_SubCategory_Code',
  'Prod_Description',
  'Gross_Sales',
  'Total_Units',
  'Royalty_Sales',
  'MRU_Units',
  'Associated_Inst',
  'Retailer_Name',
  'CLC_Retailer_Code',
  'Address',
  'City',
  'State',
  'Zip',
  'Invoice_Date',
  'Invoice_Number',
  'UPI'
];

const CANCELLED = 'Cancelled';
const COMPLETED = 'Completed';

export function isDesignOnlyType(type) {
  const t = String(type || '').toLowerCase();
  return t === 'design_only' || t === 'design only';
}

/** Match key. Reprint lines use the original design name. */
export function designKey(name) {
  return String(name || '')
    .replace(/^reprint\s*-\s*/i, '')
    .trim()
    .toLowerCase();
}

export function canonicalDesignName(name) {
  return String(name || '').replace(/^reprint\s*-\s*/i, '').trim();
}

export function toJsDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number' || typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function monthKeyFromDate(date) {
  const d = toJsDate(date);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** The reporting period that just closed. CLC wants that month, due on the 20th after. */
export function previousMonthKey(now = new Date()) {
  return monthKeyFromDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
}

export function formatMonthLabel(monthKey) {
  const [y, m] = String(monthKey || '').split('-').map(Number);
  if (!y || !m) return monthKey || '';
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Payment is due the 20th of the month after the reporting period. */
export function dueDateForMonth(monthKey) {
  const [y, m] = String(monthKey || '').split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m, 20);
}

export function formatLongDate(date) {
  const d = toJsDate(date);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function formatInvoiceDate(date) {
  const d = toJsDate(date);
  if (!d) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
}

export function round2(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function formatSales(n) {
  return round2(n).toFixed(2);
}

export function formatUnits(n) {
  return String(Math.round(Number(n) || 0));
}

/** CLC rejects commas anywhere in the file, including text. */
export function cleanText(value) {
  return String(value ?? '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
}

export function hasComma(value) {
  return String(value ?? '').includes(',');
}

export function normalizeAssociatedInst(value) {
  return String(value || '')
    .split(';')
    .map((part) => part.replace(/,/g, ' ').trim())
    .filter(Boolean)
    .join(';');
}

export function invoiceInstant(order) {
  return toJsDate(order?.invoicedAt) || toJsDate(order?.completedAt);
}

export function orderQualifiesForReport(order, monthKey) {
  if (!order || order.status === CANCELLED) return false;
  const invoiced = order.invoiced === true || order.status === COMPLETED || order.archived === true;
  if (!invoiced) return false;
  const when = invoiceInstant(order);
  if (!when) return false;
  return monthKeyFromDate(when) === monthKey;
}

export function lineUnits(line) {
  const stored = Number(line?.totalQuantity);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const sizes = line?.sizes || {};
  return Object.values(sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
}

export function lineGross(line, units) {
  const stored = Number(line?.totalLinePrice);
  if (Number.isFinite(stored) && stored > 0) return round2(stored);
  const unit = Number(line?.pricePerPiece);
  if (Number.isFinite(unit) && unit > 0) return round2(unit * units);
  return 0;
}

export function retailerProblems(settings) {
  const problems = [];
  if (!cleanText(settings?.retailerName)) problems.push('retailer name');
  if (!cleanText(settings?.clcRetailerCode)) problems.push('CLC retailer code');
  if (!cleanText(settings?.address)) problems.push('street address');
  if (!cleanText(settings?.city)) problems.push('city');
  const state = cleanText(settings?.state);
  if (!/^[A-Za-z]{2}$/.test(state)) problems.push('2-letter state');
  if (!cleanText(settings?.zip)) problems.push('ZIP');
  return problems;
}

export function findRate(rates, institution, category) {
  const inst = String(institution || '').trim().toLowerCase();
  const cat = String(category || '').trim().toLowerCase();
  const sameInst = (rates || []).filter(
    (rate) => String(rate.institutionShortCode || '').trim().toLowerCase() === inst
  );
  const exact = sameInst.find(
    (rate) => String(rate.categoryCode || '').trim().toLowerCase() === cat
  );
  if (exact) return exact;
  return sameInst
    .filter((rate) => {
      const code = String(rate.categoryCode || '').trim().toLowerCase();
      return code && cat.startsWith(code);
    })
    .sort((a, b) => String(b.categoryCode).length - String(a.categoryCode).length)[0] || null;
}

/**
 * One price per order line, so the whole line is either royalty sales or MR/U units.
 * Missing rate: treat the full wholesale as royalty sales. CLC still computes the check.
 */
export function splitRoyaltySales(gross, units, rate) {
  const g = round2(gross);
  const u = Math.round(Number(units) || 0);
  const percent = Number(rate?.royaltyPercent);
  const mru = Number(rate?.mruAmount);
  if (!rate || !percent || percent <= 0 || !mru || mru <= 0 || u <= 0) {
    return { royaltySales: g, mruUnits: 0, mruApplied: false, threshold: null };
  }
  const threshold = mru / (percent / 100);
  const unitPrice = g / u;
  if (unitPrice < threshold) {
    return { royaltySales: 0, mruUnits: u, mruApplied: true, threshold };
  }
  return { royaltySales: g, mruUnits: 0, mruApplied: true, threshold };
}

function designLabel(design) {
  const note = cleanText(design.garmentNote);
  const cat = cleanText(design.categorySubCategoryCode);
  const upi = cleanText(design.upi);
  return [upi, cat, note].filter(Boolean).join(' · ');
}

function matchDesigns(line, designs) {
  const chosenId = line?.spLicenseDesignId;
  if (chosenId) {
    const chosen = (designs || []).find((d) => d.id === chosenId);
    if (chosen) return { design: chosen, choices: [] };
  }
  const key = designKey(line?.designName);
  const matches = (designs || []).filter((d) => d.designKey === key || designKey(d.designName) === key);
  if (matches.length === 1) return { design: matches[0], choices: [] };
  if (matches.length > 1) return { design: null, choices: matches };
  return { design: null, choices: [] };
}

export function buildReportRows({ orders, linesByOrderId, designs, rates }) {
  const rows = [];
  (orders || []).forEach((order) => {
    const lines = linesByOrderId?.[order.id] || [];
    lines.forEach((line) => {
      if (isDesignOnlyType(line.type)) return;
      const units = lineUnits(line);
      if (units <= 0) return;

      const issues = [];
      const when = invoiceInstant(order);
      const invoiceDate = formatInvoiceDate(when);
      const invoiceNumber = cleanText(order.poNumber || order.orderNumber || '');
      const gross = lineGross(line, units);
      const { design, choices } = matchDesigns(line, designs);

      if (!invoiceDate) issues.push('Missing invoice date');
      if (!invoiceNumber) issues.push('Missing invoice number');
      if (gross <= 0) issues.push('Line price is zero');

      let royaltySales = gross;
      let mruUnits = 0;
      let mruNote = '';

      if (!design && choices.length > 1) {
        issues.push('Same design name is on more than one licensed item. Choose which one.');
      } else if (!design) {
        issues.push('No UPI for this design');
      } else {
        if (!cleanText(design.upi)) issues.push('Licensed item is missing a UPI');
        if (!cleanText(design.licenseCode)) issues.push('Licensed item is missing a license code');
        if (!cleanText(design.institutionShortCode)) issues.push('Licensed item is missing a school code');
        if (!cleanText(design.categorySubCategoryCode)) issues.push('Licensed item is missing a category');
        if (!cleanText(design.prodDescription)) issues.push('Licensed item is missing a description');
        const rate = findRate(rates, design.institutionShortCode, design.categorySubCategoryCode);
        const split = splitRoyaltySales(gross, units, rate);
        royaltySales = split.royaltySales;
        mruUnits = split.mruUnits;
        if (!rate) {
          mruNote = 'No minimum-royalty rate on file. Full price is royalty sales.';
        } else if (split.mruApplied && split.mruUnits > 0) {
          mruNote = `Unit price is under the $${split.threshold.toFixed(2)} minimum-royalty threshold.`;
        }
      }

      const csv = issues.length === 0 ? {
        License_Code: cleanText(design.licenseCode),
        Institution_Short_Code: cleanText(design.institutionShortCode),
        Category_SubCategory_Code: cleanText(design.categorySubCategoryCode),
        Prod_Description: cleanText(design.prodDescription),
        Gross_Sales: formatSales(gross),
        Total_Units: formatUnits(units),
        Royalty_Sales: formatSales(royaltySales),
        MRU_Units: formatUnits(mruUnits),
        Associated_Inst: normalizeAssociatedInst(design.associatedInst),
        Invoice_Date: invoiceDate,
        Invoice_Number: invoiceNumber,
        UPI: cleanText(design.upi)
      } : null;

      rows.push({
        lineId: line.id,
        orderId: order.id,
        designName: canonicalDesignName(line.designName) || 'Untitled design',
        invoiceNumber: invoiceNumber || '—',
        invoiceDate: invoiceDate || '—',
        invoiceSort: when ? when.getTime() : 0,
        units,
        gross,
        royaltySales,
        mruUnits,
        upi: design ? cleanText(design.upi) : '',
        institution: design ? cleanText(design.institutionShortCode) : '',
        category: design ? cleanText(design.categorySubCategoryCode) : '',
        issues,
        mruNote,
        choices: choices.map((choice) => ({ id: choice.id, label: designLabel(choice) })),
        selectedDesignId: design?.id || '',
        csv
      });
    });
  });

  rows.sort((a, b) => a.invoiceSort - b.invoiceSort || String(a.invoiceNumber).localeCompare(String(b.invoiceNumber)));
  return rows;
}

export function rowsToCsv(rows, settings) {
  const retailer = {
    Retailer_Name: cleanText(settings?.retailerName),
    CLC_Retailer_Code: cleanText(settings?.clcRetailerCode),
    Address: cleanText(settings?.address),
    City: cleanText(settings?.city),
    State: cleanText(settings?.state).toUpperCase(),
    Zip: cleanText(settings?.zip)
  };
  const lines = [CLC_HEADERS.join(',')];
  rows.forEach((row) => {
    if (!row.csv) return;
    const record = { ...row.csv, ...retailer };
    lines.push(CLC_HEADERS.map((header) => record[header] ?? '').join(','));
  });
  return lines.join('\r\n');
}

export function validateDesign(input) {
  const designName = String(input?.designName || '').trim();
  if (!designName) return 'Design name is required.';
  if (hasComma(designName)) return 'Design name cannot contain commas.';
  if (!cleanText(input?.upi)) return 'UPI is required.';
  if (hasComma(input?.upi)) return 'UPI cannot contain commas.';
  if (!cleanText(input?.licenseCode)) return 'License code is required.';
  if (!cleanText(input?.institutionShortCode)) return 'School code is required.';
  if (!cleanText(input?.categorySubCategoryCode)) return 'Category code is required.';
  if (!cleanText(input?.prodDescription)) return 'Product description is required.';
  const commaField = ['upi', 'licenseCode', 'institutionShortCode', 'categorySubCategoryCode', 'prodDescription', 'garmentNote', 'associatedInst']
    .find((field) => hasComma(input?.[field]));
  if (commaField) return 'CLC rejects commas. Remove commas before saving.';
  return null;
}

export function validateRate(input) {
  if (!cleanText(input?.institutionShortCode)) return 'School code is required.';
  if (!cleanText(input?.categoryCode)) return 'Category code is required.';
  const percent = Number(input?.royaltyPercent);
  const mru = Number(input?.mruAmount);
  if (!Number.isFinite(percent) || percent <= 0 || percent > 100) return 'Royalty percent must be between 0 and 100.';
  if (!Number.isFinite(mru) || mru < 0) return 'Minimum royalty per unit must be zero or more.';
  return null;
}

export function validateSettings(input) {
  const commaField = ['retailerName', 'clcRetailerCode', 'address', 'city', 'state', 'zip']
    .find((field) => hasComma(input?.[field]));
  if (commaField) return 'CLC rejects commas. Remove commas from the store profile.';
  const state = cleanText(input?.state);
  if (state && !/^[A-Za-z]{2}$/.test(state)) return 'State must be the two-letter code.';
  return null;
}
